// disclosure-path.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { CapicheLoggerService } from 'capiche-logger';

export type DisclosureLevel = 'paragraphIndex' | 'sentenceIndex' | 'wordIndex' | 'suggestionIndex';

export type DisclosurePath = {
  paragraphIndex: number | null;
  sentenceIndex: number | null;
  wordIndex: number | null;
  suggestionIndex: number | null;
};

export const defaultDisclosurePath: DisclosurePath = {
  paragraphIndex: null,
  sentenceIndex: null,
  wordIndex: null,
  suggestionIndex: null,
};

@Injectable({ providedIn: 'root' })
export class DisclosurePathService {
  private logger = new CapicheLoggerService('disclosure-path.service:', false);

  private readonly currentPathSubject = new BehaviorSubject<DisclosurePath>({
    paragraphIndex: null,
    sentenceIndex: null,
    wordIndex: null,
    suggestionIndex: null,
  });

  currentPath$ = this.currentPathSubject.asObservable();

  // ── Sidebar hover path — set when the user hovers over a sentence card ─────
  // [s] sets this; [we] subscribes and applies a paragraph-level PM decoration.

  private readonly hoveredPathSubject = new BehaviorSubject<DisclosurePath | null>(null);

  hoveredPath$ = this.hoveredPathSubject.asObservable().pipe(
    distinctUntilChanged(
      (a, b) => a?.paragraphIndex === b?.paragraphIndex && a?.sentenceIndex === b?.sentenceIndex,
    ),
  );

  getCurrent(): DisclosurePath {
    const currentPath: DisclosurePath = this.currentPathSubject.value;
    // this.logger.log('getCurrent:', currentPath);
    return currentPath;
  }

  setCurrent(path: DisclosurePath) {
    if (this.isCurrent(path)) {
      this.logger.log('setCurrent:', path, 'no change');
      return;
    }
    this.logger.log('setCurrent:', path);
    this.currentPathSubject.next(path);
  }

  /**
   * Unconditionally reset the current path to the all-null default.
   *
   * setCurrent() cannot be used for this because isCurrent() treats null fields
   * as wildcards — passing all-null always returns "no change" and the
   * BehaviorSubject never emits.  clearCurrent() bypasses that guard.
   */
  clearCurrent(): void {
    this.logger.log('clearCurrent');
    this.currentPathSubject.next(defaultDisclosurePath);
  }

  setHoveredPath(path: DisclosurePath): void {
    this.hoveredPathSubject.next(path);
  }

  clearHoveredPath(): void {
    this.hoveredPathSubject.next(null);
  }

  isCurrent(itemPath: DisclosurePath): boolean {
    const currentPath: DisclosurePath = this.getCurrent();
    const isCurrent: boolean =
      (itemPath.paragraphIndex === null || currentPath.paragraphIndex === itemPath.paragraphIndex) &&
      (itemPath.sentenceIndex === null || currentPath.sentenceIndex === itemPath.sentenceIndex) &&
      (itemPath.wordIndex === null || currentPath.wordIndex === itemPath.wordIndex) &&
      (itemPath.suggestionIndex === null || currentPath.suggestionIndex === itemPath.suggestionIndex);
    // this.logger.log('isCurrent:', isCurrent, 'currentPath', currentPath, 'itemPath:', itemPath);
    return isCurrent;
  }
}
